import { FC, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Slider, SliderChangeEvent, SliderSlideEndEvent } from 'primereact/slider';
import HandThumb from './HandThumb';
import svgPaths from './svgPaths';
import { SubmitButton } from './SubmitButton';

export interface ConfidenceSliderProps {
  value: number;
  onChange: (value: number) => void;
  onSubmit?: () => void;
  onHoverChange?: (isHovered: boolean) => void;
  onDraggingChange?: (isDragging: boolean) => void;
  onInteractChange?: (hasInteracted: boolean) => void;
}

// 5-point confidence scale labels (kept for parent consumption)
export const CONFIDENCE_LEVELS = [
  { value: 0, label: 'Not true for me' },
  { value: 1, label: 'A little true' },
  { value: 2, label: 'Not sure' },
  { value: 3, label: 'Mostly true' },
  { value: 4, label: 'Very true' },
];

const HAND_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(svgPaths.handSvg)}") 15 8, pointer`;
const THUMB_STATES = ['Thumb-down', 'Thumb-middown', 'Thumb-middle', 'Thumb-midhigh', 'Thumb-up'] as const;
type ThumbState = (typeof THUMB_STATES)[number];

const getThumbState = (value: number): ThumbState => THUMB_STATES[value] ?? 'Thumb-middle';

export const ConfidenceSlider: FC<ConfidenceSliderProps> = ({
  value,
  onChange,
  onSubmit,
  onHoverChange,
  onDraggingChange,
  onInteractChange,
}) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [tooltipOffset, setTooltipOffset] = useState(0);
  const [rawSliderValue, setRawSliderValue] = useState(value * 25);
  const trackRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement | null>(null);

  // Sync rawSliderValue when parent value changes externally (not during drag)
  useEffect(() => {
    if (!isDragging) {
      setRawSliderValue(value * 25);
    }
  }, [value, isDragging]);

  useLayoutEffect(() => {
    if (!handleRef.current) {
      handleRef.current = trackRef.current?.querySelector('.p-slider-handle') as HTMLElement | null;
    }
    const handle = handleRef.current;
    if (handle) {
      setTooltipOffset(handle.offsetLeft + handle.offsetWidth / 2);
    }
  }, [rawSliderValue]);

  const markInteracted = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
      onInteractChange?.(true);
    }
  }, [hasInteracted, onInteractChange]);

  // Keyboard navigation for accessibility
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isLocked) return;

      let newValue = value;
      let shouldPreventDefault = true;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          newValue = Math.max(0, value - 1);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          newValue = Math.min(4, value + 1);
          break;
        case 'Home':
          newValue = 0;
          break;
        case 'End':
          newValue = 4;
          break;
        default:
          shouldPreventDefault = false;
      }

      if (shouldPreventDefault) {
        e.preventDefault();
        if (newValue !== value) {
          markInteracted();
          onChange(newValue);
        }
      }
    },
    [isLocked, value, markInteracted, onChange],
  );

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        onDraggingChange?.(false);
      }
    };

    if (isDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp, { once: true });
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging, onDraggingChange]);

  const handleSliderChange = useCallback(
    (e: SliderChangeEvent) => {
      markInteracted();
      const sliderValue = typeof e.value === 'number' ? e.value : e.value[0];
      setRawSliderValue(sliderValue);
    },
    [markInteracted],
  );

  const handleSlideEnd = useCallback(
    (e: SliderSlideEndEvent) => {
      const sliderValue = typeof e.value === 'number' ? e.value : e.value[0];
      const nearestDiscrete = Math.round(sliderValue / 25);
      const snappedValue = nearestDiscrete * 25;
      setRawSliderValue(snappedValue);
      onChange(nearestDiscrete);
      setIsDragging(false);
      onDraggingChange?.(false);
    },
    [onChange, onDraggingChange],
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    onHoverChange?.(true);
  }, [onHoverChange]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHoverChange?.(false);
  }, [onHoverChange]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
    onDraggingChange?.(true);
  }, [onDraggingChange]);

  const handleSubmit = useCallback(() => {
    setIsLocked(true);
    setIsHovered(false);
    setIsDragging(false);
    onDraggingChange?.(false);
    onHoverChange?.(false);
    onSubmit?.();
  }, [onSubmit, onHoverChange, onDraggingChange]);

  const displayValue = isDragging ? Math.round(rawSliderValue / 25) : value;
  const thumbState = getThumbState(displayValue);
  const showTooltip = hasInteracted || isHovered;
  const cursorStyle = isDragging || isHovered ? HAND_CURSOR : 'default';

  return (
    <>
      <div
        className={`tw-pt-16 tw-relative tw-w-[354px] tw-mx-auto ${
          isLocked ? 'tw-opacity-60 tw-pointer-events-none' : ''
        }`}
        style={{ cursor: cursorStyle }}
      >
        <div
          ref={trackRef}
          className="slider-container tw-relative tw-rounded-md tw-flex tw-flex-col tw-justify-end"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          tabIndex={isLocked ? -1 : 0}
          role="slider"
          aria-label="Confidence level"
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={displayValue}
          aria-valuetext={CONFIDENCE_LEVELS[displayValue]?.label}
          aria-disabled={isLocked}
        >
          {!hasInteracted && !isHovered && (
            <div
              className="tw-absolute tw-pointer-events-none tw-z-10 tw-w-max tw-p-6 tw-bg-[#fefbf9] tw-rounded-[12px]"
              style={{
                bottom: '200%',
                left: `${tooltipOffset}px`,
                transform: 'translateX(-50%)',
              }}
            >
              <div className="tw-flex tw-justify-center tw-text-lg tw-font-atkinson tw-leading-7">
                Use the slider to answer
              </div>
              <div className="tw-absolute -tw-bottom-[10px] tw-left-1/2 -tw-translate-x-1/2">
                <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
                  <path d="M10 10L0 0H20L10 10Z" fill="#FEFBF9" />
                </svg>
              </div>
            </div>
          )}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                key="tooltip"
                initial={{ opacity: 0, y: 5, scale: 0.9, x: '-50%' }}
                animate={{ opacity: 1, y: 0, scale: isHovered ? 1.05 : 1, x: '-50%' }}
                exit={{ opacity: 0, y: 5, scale: 0.9, x: '-50%' }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="tw-absolute tw-pointer-events-none tw-z-10"
                style={{
                  left: `${tooltipOffset}px`,
                  bottom: '200%',
                }}
              >
                <div className="tw-bg-[#fefbf9] tw-rounded-[12px] tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1),0px_1px_3px_0px_rgba(0,0,0,0.1)] tw-size-[56px] tw-flex tw-items-center tw-justify-center">
                  <div className=" tw-inset-0 tw-border tw-border-[#f1f0ee] tw-border-solid tw-rounded-[12px] tw-pointer-events-none" />
                  <HandThumb property1={thumbState} className="tw-relative tw-size-[30px]" />
                  <div className="tw-absolute -tw-bottom-[10px] tw-left-1/2 -tw-translate-x-1/2">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: 'rotate(-45deg)' }}>
                      <path d={svgPaths.tooltipArrow} fill="#FEFBF9" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Slider
            value={rawSliderValue}
            onChange={handleSliderChange}
            onSlideEnd={handleSlideEnd}
            step={1}
            min={0}
            max={100}
            disabled={isLocked}
            pt={{
              root: {
                className:
                  'tw-w-full tw-h-5 tw-bg-stone-200 tw-rounded-tl-sm tw-rounded-tr-2xl tw-rounded-bl-sm tw-rounded-br-2xl',
                style: { cursor: cursorStyle },
              },
              range: {
                className: 'tw-rounded-l-2xl tw-bg-indigo-600',
                style: { cursor: cursorStyle },
              },
              handle: {
                className:
                  'tw-w-7 tw-h-7 tw-left-0 tw-top-1/4 tw-absolute tw-bg-stone-0 tw-rounded-full tw-border-4 tw-border-indigo-400 touch-action-none block hover:tw-shadow-[0px_0px_0px_6px_rgba(125,145,253,0.80)] focus:tw-shadow-[0px_0px_0px_6px_rgba(125,145,253,0.80)] active:tw-shadow-[0px_0px_0px_6px_rgba(125,145,253,0.80)]',
                style: { cursor: cursorStyle, transition: 'none' },
                role: 'presentation',
                tabIndex: -1,
                'aria-hidden': true,
              },
            }}
          />
        </div>
      </div>
      <SubmitButton onClick={handleSubmit} disabled={!hasInteracted || isLocked} ariaLabel="Submit confidence rating" />
      {/* Screen reader announcement for confidence level */}
      <div className="tw-sr-only" aria-live="polite" aria-atomic="true">
        {CONFIDENCE_LEVELS[displayValue]?.label}
      </div>
    </>
  );
};
